import type {TemplateResult} from 'lit';
import {html, LitElement} from 'lit';
// @ts-ignore
import Autocomplete from '@trevoreyre/autocomplete-js';
import {localized, msg} from '@lit/localize';
import {customElement, state} from 'lit/decorators.js';

import {POILayersInfo, ROUTE_LAYERS} from '../layers/layerConstants.ts';
import {getLanguageCode} from '../i18n.ts';
import {BASE_API_URL} from '../envs.ts';
import {classMap} from 'lit/directives/class-map.js';
import {ElementSearchResult} from './element-search-result.ts';
import {
  createRouteURL,
  createSegmentURL,
  createMapUrlFromBbox,
} from '../urlHandling/urlCreate.ts';
import {
  getURLSearchParams,
  isSVGLogoEnabled,
} from '../urlHandling/urlSearchParams.ts';
import {overlayDirectUrlFragment} from '../urlHandling/urlParams.ts';
import {Router} from '@vaadin/router';
import {crossSvg, divider, searchSvg} from '../helpers/elements.ts';
import {replaceAll} from '../helpers/string.ts';
import {WS_I18N} from '../layers/layerTranslations.ts';
import type {
  LegacyUserLand,
  RouteCategoryCode,
  SeasonCode,
} from '../layers/layerTypes.ts';
import {landToLandCode} from '../layers/layerHelpers.ts';
import {sendAnalytics} from '../analytics.ts';
import {
  mapElementConnected$,
  isMapPage$,
  showPlaceFromSearch$,
  edittrackMode$,
} from '../stores/store-map.ts';
import {EXCLUDED_SEARCH_RESULTS} from '../helpers/constants.ts';
import type {Subscription} from 'rxjs';

type GeoAdminSearchResultProperties = {
  detail: string;
  geom_quadindex: string;
  geom_st_box2d: string;
  id: number;
  label: string;
  lat: number;
  lon: number;
  num: number;
  objectclass: string;
  origin: string;
  rank: number;
  weight: number;
  x: number;
  y: number;
  zoomlevel: number;
};

type GeoAdminSearchResult = {
  bbox: number[];
  geometry: {coordinates: number[]; type: string};
  id: number;
  properties: GeoAdminSearchResultProperties;
  type: string;
};

type FTSSearchSource = {
  title: string;
  title_admin?: string;
  kind: string;
  start: string;
  via?: string;
  end: string;
  category?: RouteCategoryCode;
  number?: number;
  route_number?: number;
  panel_hasnumber?: boolean;
  address_city: string;
  signal_way?: string;
  types?: string[];
  geo_point?: {coordinates: number[]; type: string};
  logo?: string;
  logo_svg?: string;
};

type POIKinds =
  | 'accommodation'
  | 'cycleservice'
  | 'place'
  | 'sightseeing'
  | 'velorental';

type FTSSearchResultBase = {
  id: string;
  _score: number;
  _source: FTSSearchSource;
};

type FTSSearchResultPoi = FTSSearchResultBase & {
  kind: POIKinds | 'mountaineering' | 'ski_tour';
  _type: 'poi';
};

type FTSSearchResultSwisspark = FTSSearchResultBase & {
  kind: 'swisspark';
  _type: 'swisspark';
};

type FTSSearchResultRoute = FTSSearchResultBase & {
  kind: LegacyUserLand;
  _type: 'route' | 'segment';
};

type FTSSearchResult =
  | FTSSearchResultRoute
  | FTSSearchResultPoi
  | FTSSearchResultSwisspark;

type FTSResultBase = {
  type: 'fts';
  id: string;
  groupTitle: () => TemplateResult | string;
  addressCity?: string;
};

type FTSResultPoi = FTSResultBase & {
  ftsType: 'poi';
  kind: POIKinds;
};

type FTSResultSwisspark = FTSResultBase & {
  ftsType: 'swisspark';
  kind: 'swisspark';
};

type FTSResultRoute = FTSResultBase & {
  ftsType: 'route' | 'segment';
  category: RouteCategoryCode;
  number: number;
  routeNumber?: number;
  kind: LegacyUserLand;
  logo: string;
  start: string;
  via?: string;
  end: string;
};

type FTSResult = FTSResultPoi | FTSResultSwisspark | FTSResultRoute;

export type GeoadminResult = {
  type: 'geoadmin';
  bbox: number[];
  coordinates: [number, number];
  groupTitle: () => TemplateResult | string;
};

export type SearchResult<T = FTSResult | GeoadminResult> = {
  title: string;
  attributes: T;
  index: number;
};

export type AutocompleteProps = {
  class: string;
  'data-result-index': number;
  id: string;
  role: any; // FIXME: where can we find the actual list of roles?
};

type FilterCode = 'all' | SeasonCode | 'onMap';

type ListInfo = Record<
  LegacyUserLand | POIKinds | 'swisspark' | 'geoadmin',
  {length: number; showAll: boolean}
>;

const FILTERS: {label: () => string; code: FilterCode}[] = [
  {label: () => msg('All'), code: 'all'},
  {label: () => msg('Summer'), code: 'summer'},
  {label: () => msg('Winter'), code: 'winter'},
  {label: () => msg('Places, place names and streets'), code: 'onMap'},
];

const DEFAULT_INDEXES: Record<LegacyUserLand | POIKinds | 'swisspark', number> =
  {
    accommodation: -1,
    crosscountry: -1,
    cycleservice: -1,
    kanu: -1,
    mountainbike: -1,
    place: -1,
    sightseeing: -1,
    skating: -1,
    sledging: -1,
    snowshoe: -1,
    swisspark: -1,
    velo: -1,
    velorental: -1,
    wander: -1,
    winterhiking: -1,
  };

// new object creates to prevent mutation
const getDefaultListInfo = (): ListInfo => {
  return {
    geoadmin: {length: 0, showAll: false},
    accommodation: {
      length: 0,
      showAll: false,
    },
    crosscountry: {length: 0, showAll: false},
    cycleservice: {length: 0, showAll: false},
    kanu: {length: 0, showAll: false},
    mountainbike: {length: 0, showAll: false},
    place: {length: 0, showAll: false},
    sightseeing: {length: 0, showAll: false},
    skating: {length: 0, showAll: false},
    sledging: {length: 0, showAll: false},
    snowshoe: {length: 0, showAll: false},
    swisspark: {length: 0, showAll: false},
    velo: {length: 0, showAll: false},
    velorental: {length: 0, showAll: false},
    wander: {length: 0, showAll: false},
    winterhiking: {length: 0, showAll: false},
  };
};

// new object creates to prevent mutation
const getEmptyOrderedGroups = (): Record<
  LegacyUserLand | POIKinds | 'swisspark' | 'geoadmin',
  SearchResult[]
> => {
  return {
    wander: [],
    velo: [],
    mountainbike: [],
    skating: [],
    kanu: [],
    winterhiking: [],
    snowshoe: [],
    crosscountry: [],
    sledging: [],
    geoadmin: [],
    accommodation: [],
    place: [],
    sightseeing: [],
    velorental: [],
    cycleservice: [],
    swisspark: [],
  };
};

@localized()
@customElement('element-search')
export class ElementSearch extends LitElement {
  @state() filters = {
    summer: false,
    winter: false,
    onMap: false,
  };
  @state() counter: Record<FilterCode, number> = {
    all: 0,
    summer: 0,
    winter: 0,
    onMap: 0,
  };
  @state() inputValue = '';
  @state() isTourEditing = false;
  listInfo: ListInfo = getDefaultListInfo();
  autocomplete: Autocomplete;
  private mapConnected = false;
  private analyticsSearchTimeout: NodeJS.Timeout | undefined = undefined;
  private subscriptions: Subscription[] = [];

  override firstUpdated() {
    this.autocomplete = new Autocomplete(this, {
      debounceTime: 200,
      search: (input: string): Promise<SearchResult[]> => {
        return new Promise((resolve) => {
          const urls: string[] = [];
          if (input.length >= 1) {
            if (this.analyticsSearchTimeout) {
              clearTimeout(this.analyticsSearchTimeout);
            }
            this.analyticsSearchTimeout = setTimeout(() => {
              sendAnalytics('event', 'search_query', {
                event_label: this.inputValue,
              });
            }, 2000);

            const locationUrl =
              'https://api3.geo.admin.ch/rest/services/api/SearchServer' +
              '?geometryFormat=geojson&sr=2056&lang={lang}&searchText={input}&type=locations&origins=kantone,district,zipcode,gg25,gazetteer,address';
            urls.push(locationUrl);
            urls.push(
              `${BASE_API_URL}/api/4/fts_search?qtext={input}&limit=1000&lang={lang}&season=all`,
            );
            const promises = urls.map((url) => {
              url = url
                .replace('{lang}', getLanguageCode())
                .replace('{input}', input);
              return fetch(url)
                .then((response) => response.json())
                .then(
                  (featureCollection) =>
                    featureCollection.features || featureCollection,
                );
            });

            Promise.all(promises).then((results) => {
              const groupedResults = getEmptyOrderedGroups();
              if (results.length && results[0].length) {
                const geoResults: SearchResult[] = results[0]
                  .filter(
                    (r: GeoAdminSearchResult) =>
                      !EXCLUDED_SEARCH_RESULTS.includes(
                        r.properties.objectclass,
                      ),
                  )
                  .map(
                    (r: GeoAdminSearchResult, index: number): SearchResult => {
                      return {
                        title: r.properties.label,
                        attributes: {
                          type: 'geoadmin',
                          coordinates: [
                            r.geometry.coordinates[1],
                            r.geometry.coordinates[0],
                          ],
                          bbox: r.bbox,
                          groupTitle: () =>
                            msg('Places, place names and streets'),
                        },
                        index,
                      };
                    },
                  );
                this.listInfo.geoadmin.length = geoResults.length;
                groupedResults.geoadmin.push(...geoResults);
              }
              if (results.length && results[1].length) {
                const searchResults: FTSSearchResult[] = results[1];
                const indexes = {...DEFAULT_INDEXES};
                searchResults.forEach((r: FTSSearchResult) => {
                  if (r.kind !== 'mountaineering' && r.kind !== 'ski_tour') {
                    indexes[r.kind] = indexes[r.kind] + 1;
                    this.listInfo[r.kind].length = indexes[r.kind] + 1;
                    const result = this.ftsResultFactory(r, indexes[r.kind]);
                    if (result) groupedResults[r.kind].push(result);
                  }
                });
              }
              const flatResult: SearchResult[] = [];
              Object.values(groupedResults).forEach((r) =>
                flatResult.push(...r),
              );
              const summerResults = this.filterBySeason(flatResult, 'summer');
              const winterResults = this.filterBySeason(flatResult, 'winter');
              const onMapResults = flatResult.filter(
                (r) => r.attributes.type === 'geoadmin',
              );
              this.counter = {
                all: flatResult.length,
                summer: summerResults.length,
                winter: winterResults.length,
                onMap: onMapResults.length,
              };
              if (this.filters.onMap) resolve(onMapResults);
              else if (this.filters.summer) resolve(summerResults);
              else if (this.filters.winter) resolve(winterResults);
              else resolve(flatResult);
            });
          } else {
            resolve([]);
          }
        });
      },

      renderResult: (result: SearchResult, props: AutocompleteProps) => {
        const info =
          this.listInfo[
            result.attributes.type === 'geoadmin'
              ? 'geoadmin'
              : result.attributes.kind
          ];
        if (result.index > 2 && !info.showAll) return '';
        const resultElem = new ElementSearchResult();
        resultElem.props = props;
        resultElem.result = result;
        resultElem.parent = this;
        resultElem.className = 'col-span-10 xxl:col-span-8';
        return resultElem;
      },

      getResultValue: (result: SearchResult) => {
        return replaceAll(replaceAll(result.title, '<b>', ''), '</b>', '');
      },

      onSubmit: (result: SearchResult) => {
        if (!result) return;
        const attrs = result.attributes;
        const lang = getLanguageCode();
        if (attrs.type === 'fts') {
          sendAnalytics('event', 'search_select', {
            event_label: `${attrs.kind}:${result.title}`,
          });
          if (attrs.ftsType === 'route' || attrs.ftsType === 'segment') {
            const url =
              attrs.ftsType === 'route'
                ? createRouteURL(lang, landToLandCode[attrs.kind], attrs.number)
                : createSegmentURL(
                    lang,
                    landToLandCode[attrs.kind],
                    attrs.routeNumber,
                    attrs.number,
                  );
            Router.go(
              `${url}${this.mapConnected ? '?keepLayers&noScroll' : ''}`,
            );
          } else if (attrs.ftsType === 'poi' || attrs.ftsType === 'swisspark') {
            const layerName = overlayDirectUrlFragment[attrs.kind][lang];
            const url = `${location.origin}/${lang}/${layerName}-${attrs.id}`;
            Router.go(`${url}${this.mapConnected ? '?keepLayers' : ''}`);
          }
        } else if (attrs.type === 'geoadmin') {
          sendAnalytics('event', 'search_select', {
            event_label: `geoadmin:${result.title}`,
          });
          if (isMapPage$.getValue()) {
            showPlaceFromSearch$.next({
              pointCoordinates: attrs.coordinates,
            });
          } else {
            const url = createMapUrlFromBbox(
              lang,
              getURLSearchParams(),
              attrs.bbox,
              this.mapConnected,
              attrs.coordinates,
            );
            Router.go(url);
          }
        }
        this.closeSearch();
      },
    });

    this.autocomplete.input.removeEventListener(
      'blur',
      this.autocomplete.core.handleBlur,
    );
    this.autocomplete.input.focus();
    this.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') this.closeSearch();
    });

    // Keep search results displayed after enter or tab
    this.querySelector('input').addEventListener('keydown', (e) => {
      if (e.code === 'Enter' || e.code === 'Tab')
        this.autocomplete.core.updateResults(this.autocomplete.input.value);
    });
  }

  showAll(category: LegacyUserLand | POIKinds | 'swisspark' | 'geoadmin') {
    this.listInfo[category].showAll = true;
    this.autocomplete.core.updateResults(this.autocomplete.input.value);
  }

  showLess(category: LegacyUserLand | POIKinds | 'swisspark' | 'geoadmin') {
    this.listInfo[category].showAll = false;
    this.autocomplete.core.updateResults(this.autocomplete.input.value);
  }

  filterBySeason(results: SearchResult[], season: SeasonCode): SearchResult[] {
    return results.filter((r) => {
      const attrs = r.attributes;
      if (attrs.type !== 'fts') return false;
      if (attrs.ftsType === 'route') {
        const code = landToLandCode[attrs.kind];
        return code && ROUTE_LAYERS[code].season === season;
      } else if (attrs.ftsType === 'poi') {
        const layer = POILayersInfo.find((l) => l.code === attrs.kind);
        return layer && layer[season];
      }
      return attrs.ftsType === 'swisspark';
    });
  }

  chooseFilter(filter: FilterCode) {
    this.filters = {
      winter: filter === 'winter',
      summer: filter === 'summer',
      onMap: filter === 'onMap',
    };
    this.resetListInfo();
    this.autocomplete.core.updateResults(this.autocomplete.input.value);
  }

  ftsResultFactory(r: FTSSearchResult, index: number): SearchResult<FTSResult> {
    if (r.kind === 'mountaineering' || r.kind === 'ski_tour') {
      return undefined;
    }
    switch (r._type) {
      case 'route':
      case 'segment':
        return {
          title: r._source.title || r._source.title_admin,
          attributes: {
            type: 'fts',
            ftsType: r._type,
            kind: r.kind,
            id: r.id.split('##')[1],
            number: r._source.number,
            routeNumber: r._source.route_number,
            category: r._source.category,
            groupTitle: ROUTE_LAYERS[landToLandCode[r.kind]].getFlatTitle,
            logo: isSVGLogoEnabled() ? r._source.logo_svg : r._source.logo,
            start: r._source.start,
            via: r._source.via,
            end: r._source.end,
          },
          index,
        };
      case 'poi':
        return {
          title: r._source.title,
          attributes: {
            type: 'fts',
            ftsType: r._type,
            kind: r.kind,
            id: r.id.split('##')[1],
            groupTitle: () => WS_I18N[<POIKinds>r.kind], // cast because TS doesn't see condition above
            addressCity: r._source.address_city,
          },
          index,
        };
      case 'swisspark':
        return {
          title: r._source.title,
          attributes: {
            type: 'fts',
            ftsType: r._type,
            kind: r.kind,
            id: r.id.split('##')[1],
            groupTitle: () => WS_I18N[r.kind],
            addressCity: r._source.address_city,
          },
          index,
        };
      default:
        console.error(`Missing search type: ${(<FTSSearchResult>r)._type}`);
        return undefined;
    }
  }

  isFilterSelected(filter: FilterCode) {
    if (filter === 'all') return !Object.values(this.filters).find((v) => v);
    else return this.filters[filter];
  }

  resetListInfo() {
    this.listInfo = getDefaultListInfo();
  }

  closeSearch() {
    this.dispatchEvent(new CustomEvent('close'));
  }

  inputChanged(e: InputEvent) {
    this.resetListInfo();
    this.inputValue = (e.target as HTMLInputElement).value;
  }

  resetInputField() {
    this.querySelector('input').value = '';
    this.autocomplete.core.updateResults(this.autocomplete.input.value);
    this.inputValue = '';
  }

  override connectedCallback() {
    super.connectedCallback();
    if (this.autocomplete?.input) this.autocomplete.input.focus();
    this.subscriptions.push(
      mapElementConnected$.subscribe((value) => {
        this.mapConnected = value;
      }),
    );
    this.subscriptions.push(
      edittrackMode$.subscribe((mode) => {
        this.isTourEditing = mode === 'edit';
        if (this.isTourEditing)
          this.filters = {
            winter: false,
            summer: false,
            onMap: true,
          };
      }),
    );
  }

  override disconnectedCallback(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.disconnectedCallback();
  }

  override render() {
    return html`
      <div class="w-full shadow-search">
        <div class="w-content mx-auto grid grid-cols-10 gap-x-4">
          <div class="col-span-10 flex h-[80px] items-center justify-center">
            ${searchSvg(
              'h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l mr-[22px] shrink-0',
            )}
            <input
              type="text"
              class="field-font h-[56px] min-w-[75px] flex-auto shrink p-4 outline-none"
              placeholder="${msg('Search…')}"
              @input="${(e: InputEvent) => this.inputChanged(e)}"
            />
            <button
              class="-ml-[32px] mr-[16px] md:-ml-[40px] md:mr-[20px]"
              @click="${() => this.resetInputField()}"
            >
              ${crossSvg(
                'h-logo-chm-plus md:h-header-icon-s fill-chm-red mouse:hover:fill-chm-focus-red cursor-pointer',
              )}
            </button>
            <button class="ml-[20px]" @click="${() => this.closeSearch()}">
              ${crossSvg(
                'h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l fill-chm-red mouse:hover:fill-chm-focus-red cursor-pointer',
              )}
            </button>
          </div>
          ${divider(
            '100vw',
            '1px',
            '0 0',
            '#DEE2E6',
            'absolute z-50 left-0 top-[80px]',
          )}
          <div
            class="col-span-10 ml-[42px] flex items-center justify-center bg-white py-[8px] sm:ml-[54px] md:pt-[13px] mdxx:ml-[70px]"
          >
            <div class="w-content flex flex-wrap items-center gap-[8px]">
              ${FILTERS.map(
                (filter) =>
                  html` <button
                    class="${classMap({
                      'search-filter-selected': this.isFilterSelected(
                        filter.code,
                      ),
                      'search-filter': !this.isFilterSelected(filter.code),
                      'search-filter-disabled':
                        this.isTourEditing && filter.code !== 'onMap',
                    })} flex h-[32px] cursor-pointer select-none items-center rounded-full px-[16px] py-[7px] text-search-filter text-body sm:text-md-body"
                    @click="${() => this.chooseFilter(filter.code)}"
                  >
                    ${filter.label()}
                    ${this.inputValue.length
                      ? html`(${this.counter[filter.code]})`
                      : ''}
                  </button>`,
              )}
            </div>
          </div>
          <div class="w-content">
            <ul
              class="grid max-h-[70vh] !w-[inherit] max-w-[inherit] grid-cols-10 gap-4 overflow-y-auto bg-white shadow-search"
            ></ul>
          </div>
        </div>
      </div>
    `;
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-search': ElementSearch;
  }
}
